import React, { useEffect, useRef, useState } from 'react'

import Button from '../ButtonNew'
import UploadCardTopBgImg from '../../assets/images/care-plans/upload-card-top-bg.png'
import Text from '../Text'
import { getThumbnail } from '../../utils/helpers/common'
import FileUploadBottomSheet from '../BottomSheet/FileUploadBottomSheet'
import { documentTypeTitle } from '../../utils/helpers/file'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from '../../store/slices/index'
import UploadRecordSuccessModal from '../Modal/UploadRecordSuccessModal'
import moment from 'moment'
import { getEncryptedText } from '../../api/base'
import DefaultDocumentImg from '../../assets/images/care-plans/default-record-preview.svg'
import Calender from '../Icons/Calender'
import ThreeDotsIcon from '../../assets/icons/three-dots.svg'
import MoreOptionsBottomSheet from '../BottomSheet/MoreOptionsBottomSheet'
import Lottie from 'lottie-react'
import * as underReviewAnimationData from '../../assets/animations/under-review.json'
import DocumentPreviewModal from '../Modal/DocumentPreviewModal'
import ReasonSheet from '../BottomSheet/ReasonSheet'
import { supportProgramStatus } from '../../constants/support-programs'
import AtomicLoader from '../Loader/AtomicLoader'

const renderTopPill = (documentStatus) => {
  const underReviewAnimationOptions = {
    loop: true,
    autoplay: true,
    animationData: underReviewAnimationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  }

  switch (documentStatus) {
    case 'not_uploaded':
      return (
        <div className="px-4 py-1 rounded-full border border-gray-600 bg-gray-50 absolute top-4 left-4">
          <Text variant="body" size="small" tag="p" className="text-gray-600">
            Step {documentType === 'ds' ? 1 : 2}
          </Text>
        </div>
      )
    case 'under_review':
      return (
        <div className="px-4 py-1 rounded-full border border-yellow-600 bg-yellow-50 absolute top-4 left-4">
          <Text variant="body" size="small" tag="p" className="text-yellow-600">
            Under Review
          </Text>
          {/* <Lottie
            options={underReviewAnimationOptions}
            height={80}
            width={100}
          /> */}
        </div>
      )
    case 'approved':
      return (
        <div className="px-4 py-1 rounded-full border border-green-600 bg-green-50 absolute top-4 left-4">
          <Text variant="body" size="small" tag="p" className="text-green-600">
            Verified
          </Text>
        </div>
      )
    case 'pending':
      return (
        <div className="px-4 py-1 rounded-full border border-gray-400 bg-gray-50 absolute top-4 left-4">
          <Text variant="body" size="small" tag="p" className="text-gray-400">
            Pending
          </Text>
        </div>
      )
    case 'rejected':
      return (
        <div className="px-4 py-1 rounded-full border bg-[#E54848] absolute top-4 left-4">
          <Text variant="body" size="small" tag="p" className="text-white">
            Rejected
          </Text>
        </div>
      )
    default:
      return <></>
  }
}

const DocumentView = ({
  document,
  documentType,
  drugName,
  documentStatus = 'pending',
  uploadReportType = 'prostate_cancer_report',
  visitId = '',
  onUploadSuccess,
}) => {
  console.log('documentStatus', documentStatus)
  const [thumbnail, setThumbnail] = useState('')
  const [thumbnailLoading, setThumbnailLoading] = useState(true)
  const [uploadAgain, setUploadAgain] = useState(false)
  const [showFileUploadBottomSheet, setShowFileUploadBottomSheet] =
    useState(false)
  const [showMoreOptionsBottomSheet, setShowMoreOptionsBottomSheet] =
    useState(false)
  const [showRejectedReasonSheet, setShowRejectedReasonSheet] = useState(false)
  const [showPreviewDocument, setShowPreviewDocument] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isFileUploading, setIsFileUploading] = useState(false)
  const dispatch = useDispatch()
  const { patientDetails } = useSelector((state) => state.auth)

  useEffect(() => {
    ;(async () => {
      if (!document || document?.document_url?.length <= 0) {
        return
      }
      setThumbnailLoading(true)
      await getThumbnail(document)
        .then((res) => {
          setThumbnail(res)
          setThumbnailLoading(false)
        })
        .catch((err) => {
          setThumbnail(null)
          setThumbnailLoading(false)
        })
    })()
  }, [document?.document_url])

  const title =
    documentType === 'dp'
      ? 'Doctor Prescription'
      : documentType === 'pi'
        ? 'Purchase Invoice'
        : ''

  const description =
    documentType === 'dp'
      ? `Upload your doctor's prescription which mentions the <b>${drugName}</b> medication`
      : documentType === 'pi'
        ? `Upload the latest purchase invoice for <b>${drugName}</b> medication`
        : ''

  const handleFileUpload = async ({ fileData }) => {
    console.log('fileData', fileData)
    try {
      setShowFileUploadBottomSheet(false)
      setShowMoreOptionsBottomSheet(false)
      setIsFileUploading(true)

      const formData = new FormData()

      let payload = {
        ...(uploadAgain && {
          test_type_id: document?.test_type_id,
          patient_records_id: document?.patient_records_id,
        }),
        title: documentTypeTitle(documentType).title,
        test_name: documentTypeTitle(documentType).test_name,
        container_account: process.env.REACT_APP_AZURE_CONTAINER_ACCOUNT,
        document_data: [
          uploadAgain
            ? document?.document_name
            : `${patientDetails?.data?.patient_id}-New-Record-${moment().unix()}.pdf`,
        ],
        container_name: 'patient-reports',
        extension_type:
          fileData?.[0]?.type !== 'application/pdf' ? 'jpg' : 'pdf',
        meta_data: {
          report_status:
            supportProgramStatus.DOCUMENT_REVIEW_STATUS.UNDER_REVIEW,
          ...(visitId.length > 0 && { visit_id: visitId }),
        },
      }

      // This will change for every PSP
      payload['meta_data'][uploadReportType] = '1'

      // Handle file upload
      const files = Array.isArray(fileData) ? fileData : [fileData]
      console.log('Files to upload:', files)

      // Append files to FormData
      for (const file of files) {
        console.log('file', file)
        formData.append('file', file)
      }

      formData.append('payload', getEncryptedText(payload))

      new Promise((resolve, reject) => {
        dispatch(
          actions.updateHealthRecordRequest({
            payload: formData,
            resolve,
            reject,
          }),
        )
      })
        .then((res) => {
          console.log('updateHealthRecordRequest', res)
          setIsFileUploading(false)
          setShowSuccess(true)
          // setTimeout(() => {
          //   setShowSuccess(false)
          //   onUploadSuccess()
          // }, 500)
        })
        .catch((error) => {
          console.log('updateHealthRecordRequest error', error)
          setIsFileUploading(false)
        })
    } catch (error) {
      console.error('Error while uploading Health Record', error)
    }
  }

  if (isFileUploading) {
    return <AtomicLoader show={true} />
  }

  // actual render of component
  if (!document) {
    return (
      <div>
        <div className="w-1/2 relative">
          <img src={UploadCardTopBgImg} className="-z-10" />
          {/* make it center  */}

          {renderTopPill(documentStatus)}
        </div>
        <div className="bg-white rounded-b-lg px-4 py-5">
          <Text
            variant="body"
            size="large"
            tag="p"
            className="font-semibold text-[#1F2933] mb-2"
            bold={'true'}
          >
            {title}
          </Text>
          <Text
            variant="body"
            size="medium"
            tag="p"
            className="text-[#3D4854] mb-10"
            dangerouslySetInnerHTML={{ __html: description }}
          />

          <Button
            fullWidth
            disabled={isFileUploading}
            loading={isFileUploading}
            onClick={() => setShowFileUploadBottomSheet(true)}
          >
            Upload Document
          </Button>

          <FileUploadBottomSheet
            show={showFileUploadBottomSheet}
            onClose={() => setShowFileUploadBottomSheet(false)}
            onFileUpload={(fileData) => handleFileUpload({ fileData })}
          />

          <UploadRecordSuccessModal
            show={showSuccess}
            onClose={() => {
              setShowSuccess(false)
              onUploadSuccess()
            }}
          />
        </div>
      </div>
    )
  } else if (documentStatus === 'rejected') {
    return (
      <div>
        <div className="w-1/2 relative">
          <img src={UploadCardTopBgImg} className="-z-10" />
          {renderTopPill(documentStatus)}
        </div>
        <div className="bg-white rounded-b-lg px-4 py-5">
          <Text
            variant="body"
            size="large"
            tag="p"
            className="font-semibold text-[#1F2933] mb-2"
            bold={'true'}
          >
            {title}
          </Text>
          <Text
            variant="body"
            size="medium"
            tag="p"
            className="text-[#3D4854] mb-10"
          >
            {title} was rejected by your Care Specialist.{' '}
            <span
              className="text-red-500"
              onClick={() => setShowRejectedReasonSheet(true)}
            >
              View Reason
            </span>
          </Text>

          <Button
            fullWidth
            disabled={isFileUploading}
            loading={isFileUploading}
            onClick={() => {
              setUploadAgain(true)
              setShowFileUploadBottomSheet(true)
            }}
          >
            Upload Again
          </Button>
        </div>

        <FileUploadBottomSheet
          show={showFileUploadBottomSheet}
          onClose={() => {
            setShowFileUploadBottomSheet(false)
          }}
          onFileUpload={(fileData) => handleFileUpload({ fileData })}
        />

        <ReasonSheet
          show={showRejectedReasonSheet}
          onClose={() => {
            setShowRejectedReasonSheet(false)
          }}
          reason={document?.record_meta_info?.rejected_reason}
        />

        <UploadRecordSuccessModal
          show={showSuccess}
          onClose={() => {
            setShowSuccess(false)
            onUploadSuccess()
          }}
        />
      </div>
    )
  } else {
    return (
      <div>
        <div className="w-1/2 relative">
          <img src={UploadCardTopBgImg} className="-z-10" />
          {/* make it center  */}
          {renderTopPill(documentStatus)}
        </div>
        <div className="bg-white rounded-b-lg px-4 py-2">
          <div className="w-full h-full">
            <button
              className="flex items-center justify-center w-full h-full"
              onClick={() => {
                setShowPreviewDocument(true)
              }}
            >
              {thumbnailLoading ? (
                <div>Loading...</div>
              ) : thumbnail ? (
                <div>
                  <img src={thumbnail} alt="thumbnail" />
                </div>
              ) : (
                <div>
                  <img
                    src={DefaultDocumentImg}
                    className="h-[124px]"
                    alt="defatult-document"
                  />
                </div>
              )}
            </button>

            <div className="pt-2">
              <div className="flex items-center justify-between">
                <Text
                  variant="body"
                  size="large"
                  tag="p"
                  className="text-[#1F2933] font-semibold"
                  bold={'true'}
                >
                  {title}
                </Text>
                <button
                  className="px-2 py-1"
                  onClick={() => setShowMoreOptionsBottomSheet(true)}
                >
                  <img
                    src={ThreeDotsIcon}
                    // className="w-3 h-3"
                    alt="three-dots"
                  />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Calender />
                <Text
                  variant="body"
                  size="medium"
                  tag="p"
                  className="text-[#3D4854]"
                >
                  {document?.created_at}
                </Text>
              </div>
            </div>
          </div>

          <MoreOptionsBottomSheet
            show={showMoreOptionsBottomSheet}
            onClose={() => setShowMoreOptionsBottomSheet(false)}
            document={{
              documentName: title,
              documentLink:
                Array.isArray(document?.document_url) &&
                document?.document_url?.length > 0
                  ? document?.document_url[0]
                  : document?.document_url,
            }}
            onUploadAgainClick={() => {
              setUploadAgain(true)
              setShowFileUploadBottomSheet(true)
            }}
            showUploadAgain={documentStatus !== 'approved'}
          />

          <DocumentPreviewModal
            show={showPreviewDocument}
            onClose={() => {
              setShowPreviewDocument(false)
            }}
            documentName={title}
            documentLink={
              Array.isArray(document?.document_url) &&
              document?.document_url?.length > 0
                ? document?.document_url[0]
                : document?.document_url
            }
          />

          <FileUploadBottomSheet
            show={showFileUploadBottomSheet}
            onClose={() => {
              setShowFileUploadBottomSheet(false)
            }}
            onFileUpload={(fileData) => handleFileUpload({ fileData })}
          />

          <UploadRecordSuccessModal
            show={showSuccess}
            onClose={() => {
              setShowSuccess(false)
              onUploadSuccess()
            }}
          />
        </div>
      </div>
    )
  }
}

export default DocumentView
